import axios from 'axios';

const MAIN_CONTROLLER_URL = process.env.REACT_APP_MAIN_CONTROLLER_URL || 'https://cont.kooraipro.com';
const SCM_APP_ID = process.env.REACT_APP_SCM_APP_ID || 'scm';
const CURRENT_APP_URL = process.env.REACT_APP_CURRENT_URL || window.location.origin;

/**
 * SSO Service for integrating with KaipropApps main controller
 */
class SSOService {
  constructor() {
    // Listen for SSO token from parent window (when embedded in iframe)
    this.setupMessageListener();
  }

  /**
   * Setup listener for postMessage from parent window
   */
  setupMessageListener() {
    window.addEventListener('message', (event) => {
      // In production, verify event.origin
      if (event.data && event.data.type === 'SSO_TOKEN') {
        console.log('📨 Received SSO token from parent window');
        const { token, user } = event.data;

        if (token) {
          localStorage.setItem('token', token);
        }

        if (user) {
          localStorage.setItem('ssoUser', JSON.stringify(user));
        }
      }
    });
  }

  /**
   * Detect SSO token from URL params or localStorage
   * @returns {string|null} - Token if found
   */
  detectSSOToken() {
    // Check URL params first (support both 'token' and 'ssoToken')
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token') || urlParams.get('ssoToken');

    if (tokenFromUrl) {
      localStorage.setItem('token', tokenFromUrl);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return tokenFromUrl;
    }

    // Check localStorage
    return localStorage.getItem('token');
  }

  /**
   * Validate token with main controller
   * @param {string} token - JWT token to validate
   * @returns {Promise<Object>} - Validation response with user data
   */
  async validateWithMainController(token) {
    try {
      const response = await axios.post(
        `${MAIN_CONTROLLER_URL}/api/auth/validate-sso`,
        {
          token,
          appId: SCM_APP_ID,
        }
      );

      return response.data;
    } catch (error) {
      console.error('SSO validation error:', error);
      throw new Error(error.response?.data?.error || 'SSO validation failed');
    }
  }

  /**
   * Sync user data from SSO to local backend
   * @param {Object} ssoUserData - User data from main controller (from postMessage)
   * @param {string} token - JWT token from main controller
   * @returns {Promise<Object>} - Local user data
   */
  async syncUserData(ssoUserData, token) {
    try {
      // Use current origin for API calls (works for both dev-scm and fact-scm)
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      const response = await axios.post(
        `${apiUrl}/api/auth/sso/login`,
        {
          ssoUserData,
          token: token || localStorage.getItem('token'),
        }
      );

      return response.data;
    } catch (error) {
      console.error('User sync error:', error);
      throw new Error(error.response?.data?.message || 'Failed to sync user data');
    }
  }

  /**
   * Redirect to main controller for SSO login
   */
  redirectToMainController() {
    const returnUrl = `${CURRENT_APP_URL}/auth/callback`;
    window.location.href = `${MAIN_CONTROLLER_URL}/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /**
   * Complete SSO login flow
   * @returns {Promise<Object>} - User data and authentication status
   */
  async completeSSOLogin() {
    try {
      // 1. Detect token
      const token = this.detectSSOToken();
      if (!token) {
        return { success: false, error: 'No token found' };
      }

      // 2. Check if we have SSO user data from postMessage
      const ssoUserStr = localStorage.getItem('ssoUser');
      let ssoUserData = null;

      if (ssoUserStr) {
        try {
          ssoUserData = JSON.parse(ssoUserStr);
          console.log('📦 Using SSO user data from postMessage:', ssoUserData);
        } catch (e) {
          console.error('Failed to parse SSO user data:', e);
        }
      }

      // 3. Sync user data with local backend (backend will validate token with main controller)
      const syncResult = await this.syncUserData(ssoUserData, token);

      // 4. Store user data
      localStorage.setItem('user', JSON.stringify(syncResult.user));
      localStorage.removeItem('ssoUser'); // Clean up temporary storage

      return {
        success: true,
        user: syncResult.user,
        token: syncResult.token, // Use the token from backend response
      };
    } catch (error) {
      console.error('SSO login error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('ssoUser');
      return {
        success: false,
        error: error.message || 'SSO login failed',
      };
    }
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  }

  /**
   * Get current user from localStorage
   * @returns {Object|null}
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }
}

export default new SSOService();
